/**
 * SimState: the COMPLETE deterministic game state (ARCHITECTURE §4–5).
 *
 * ── Invariants (every sim contributor must keep these) ───────────────────────────────────────────────────
 *  1. Plain data only: numbers, booleans, fixed-length number arrays and nested plain objects. No class
 *     instances, closures, Maps/Sets, strings that change, undefined/optional fields, or null.
 *  2. The SHAPE never changes after `createBlankState()`: never add/delete keys, never replace a nested
 *     object or array (`state.players[0] = …` is forbidden) — mutate fields in place. Arrays keep their length.
 *     This keeps `cloneInto` allocation-free and `hashState` key-order stable across peers.
 *  3. Every field is covered by `cloneInto` (src/clone.ts). A test mutates every leaf and checks the clone, so
 *     a forgotten field fails CI. Adding a field = add it to the interface, `createBlank*`, and `cloneInto`.
 *  4. Everything is derived from (initial config, input sequence). No wall clock, no Math.random, no
 *     Math.sin/cos/atan2/pow/exp/log (use @hf/shared dmath), no iteration over objects with dynamic keys.
 *  5. Coordinates: yards, y up, ground y = 0. Velocities in yd/s (integrate with DT). `facing` is +1 (right) or
 *     -1 (left). "Facing space" offsets (glove, hitboxes) are mirrored by `facing`.
 *  6. Frame counters count frames SINCE ENTERING the state, starting at 1 on the first frame in the state.
 *  7. During hit-stop (`hitStop > 0`) only `tick`, `hitStop`, the play clock and phase timers advance — plus
 *     INPUT RECORDING: `players[].input` (cur/prev/lastPress/prevPress/held) keeps recording every tick, and a
 *     press made during the freeze sets its `buffer` entry, but buffers do NOT count down while frozen. So a press
 *     made during hit-stop executes on the first frame after it (controls never feel dead after a tackle).
 */
import {
  CHARACTERS,
  DRIVE_START_YARDS_TO_GOAL,
  SIM_STATE_VERSION,
  GLOVE,
  KICK,
  KICKOFF_YARDS_TO_GOAL,
  LINEUP_GAP,
  MatchFormat,
  type CharacterIndex,
  type MatchConfig,
  type PrngState,
  RULES,
  STAMINA,
  SpecialId,
  SpecialsMode,
  YARDS_FOR_FIRST_DOWN,
  characterIndexOf,
  createPrngState,
  goalLineFor,
  resolveStats,
  xFromYardsToGoal,
} from '@hf/shared';
import {
  AfterDead,
  BallState,
  BUTTON_COUNT,
  CameraFocus,
  CommitKind,
  ConversionChoice,
  FeintKind,
  GlovePose,
  Phase,
  PlayCall,
  PlayResultKind,
  PlayerState,
  SpecialPhase,
} from './enums';

/** Bump when the SimState shape or semantics change. Lives in @hf/shared (it is part of SIM_CONTENT_HASH). */
export { SIM_STATE_VERSION };

// ── Player ─────────────────────────────────────────────────────────────────────────────────────────────

/** Per-player stat multipliers resolved at match start (ranked = all 1, base head radius). */
export interface PlayerStats {
  speedMul: number;
  jumpMul: number;
  armMul: number;
  tackleMul: number;
  tossMul: number;
  headRadius: number;
}

/**
 * Raw input history kept IN the state so presses, holds and double-taps are derived deterministically and roll
 * back with everything else. Arrays are indexed by BtnIndex (L, R, U, D, A, S).
 */
export interface InputHistory {
  /** InputFrame this tick. */
  cur: number;
  /** InputFrame last tick. */
  prev: number;
  /** Tick of the most recent press of each button (-1 = never). */
  lastPress: number[];
  /** Tick of the press before that (double-tap = lastPress - prevPress <= MOVE.doubleTapWindow). */
  prevPress: number[];
  /** Consecutive frames each button has been held (0 = up). */
  held: number[];
  /** Buffered presses: frames remaining for each button's buffered press (0 = none). See MOVE.inputBufferFrames. */
  buffer: number[];
}

export interface SpecialSim {
  /** SpecialId of this player's special (from the character, or NONE when specials are off). */
  id: number;
  phase: SpecialPhase;
  /** Frames since entering `phase`. */
  frame: number;
  /** ACTIVE effect frames remaining (0 = armed until consumed, e.g. Hail Mary's next throw). */
  timer: number;
  /**
   * Mirage decoy (only meaningful while `decoyActive`). Non-solid; popped by any lunge hitbox (DECOY_POP).
   * Render: draw it exactly like the real head INCLUDING an identical ball in its glove, but only the REAL
   * carrier casts a ball shadow, and no carrier marker is drawn while the decoy is up.
   */
  decoyActive: boolean;
  decoyX: number;
  decoyY: number;
  decoyVx: number;
  /** Falls with PHYSICS.gravity when spawned airborne. */
  decoyVy: number;
  decoyFacing: number;
  decoyFrames: number;
  /** Frames left before this special can be armed again (0 = ready), set on SPECIAL_END (specials/update.ts). */
  cooldownFrames: number;
  /** Blitz only: arms used this drive (specials/query.ts caps it at SPECIALS_TUNING.blitz.usesPerDrive). */
  usesThisDrive: number;
}

export interface PlayerSim {
  // identity (constant for the match)
  index: number;
  /** CharacterIndex (position in CHARACTER_IDS: @hf/shared `characters.ts`). */
  character: number;
  stats: PlayerStats;

  // body (head centre)
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** +1 right, -1 left. */
  facing: number;
  grounded: boolean;

  // controller
  state: PlayerState;
  /** Frames since entering `state` (1 on the first frame). */
  stateFrame: number;
  prevState: PlayerState;
  /** Frames since take-off while airborne (fake-cancel window uses this), 0 on the ground. */
  airFrame: number;
  /** True if this jump started while carrying the ball (a hurdle). */
  hurdling: boolean;
  /** Visual hop phase 0..1 while running (render squash/stretch + footstep audio sync). */
  hopPhase: number;
  /** Direction of the current/last dash (-1, 0, 1). */
  dashDir: number;
  /** Frames the player has been stopped (hesitation detection). */
  stillFrames: number;
  /** Hesitation burst frames remaining. */
  burstFrames: number;
  /** Throw charge frames held (CHARGING_THROW). */
  throwCharge: number;
  /** Up was held at release → lob. */
  lob: boolean;
  /** Truck charge frames (TRUCK_CHARGE). */
  truckCharge: number;
  /** Stun frames remaining. */
  stunFrames: number;
  /** Invulnerability frames remaining (spin). */
  iFrames: number;
  /** Whether the current spin dodged something (else whiff recovery). */
  spinDodged: boolean;
  /**
   * Frames this DEFENDER stays "beaten" after losing a counter-square exchange (truck break, stiff-arm hit,
   * Bulldozer bounce, a spin dodging his grab): non-solid and no contact grab until the carrier is past him or
   * this runs out (MOTION.beatenMaxFrames). 0 = not beaten. Owner: contact team (set + cleared in resolve.ts).
   */
  beatenFrames: number;
  /**
   * Tick this player last LOST a counter-square exchange as the defender (truck break, stiff-arm hit, Bulldozer,
   * a spin dodging him, a hurdle over his dive), -1 none. Pursuit grace: no pursuit bonus / running dive for
   * MOTION.pursuit.graceFrames after it (`pursuing`), so a won exchange is worth real yards. Reset at every snap.
   */
  beatenTick: number;

  // glove (offset relative to head centre, in FACING space)
  glovePose: GlovePose;
  gloveX: number;
  gloveY: number;
  gloveTargetX: number;
  gloveTargetY: number;

  // resources
  stamina: number;
  power: number;
  special: SpecialSim;

  // input
  input: InputHistory;

  // per-play flags (reset at every snap)
  pumpFakesUsed: number;
  hardCountsUsed: number;
  /** Has crossed the LOS this play. */
  crossedLos: boolean;
  /** Tick of this player's most recent feint, -1 none. */
  feintTick: number;
  /** FeintKind of that feint (NONE = none). Bait rules per kind: ARCHITECTURE §5 "Bait rules". */
  feintKind: FeintKind;
  /** Tick of this player's most recent commitment, -1 none (bait detection). */
  commitTick: number;
  /** CommitKind of that commitment (dive, leap, jump, hurdle, truck, spin). */
  commitKind: CommitKind;
  /**
   * Furthest x reached this play while carrying, in attackDir (power `perYard` is earned only past it). Reset to
   * the LOS at the snap and to the catch spot on a catch.
   */
  playBestX: number;
  /** Bit flags for future use (keep as a number). */
  flags: number;

  // match stats (results screen / MVP)
  statYards: number;
  statPassYards: number;
  statTackles: number;
  statCatches: number;
  statInterceptions: number;
  statSacks: number;
  statBaits: number;
  statTouchdowns: number;
}

// ── Ball ───────────────────────────────────────────────────────────────────────────────────────────────

export interface BallSim {
  state: BallState;
  /** Player index holding it (HELD), else -1. */
  holder: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Ellipse rotation, radians. */
  angle: number;
  angVel: number;
  /** Who threw the ball in flight (-1 none). */
  thrownBy: number;
  /** Frames since release. */
  airFrames: number;
  /** x where the ball was released. */
  releaseX: number;
  /** This flight is a legal forward pass (thrown from behind the LOS). */
  forwardPass: boolean;
  /** The ball has crossed the LOS (in attackDir) during this flight. */
  passedLos: boolean;
  /**
   * This flight is a field-goal / PAT toss: thrown from behind the LOS while `rules.playCall` is FG_TOSS or
   * PAT_TOSS. ONLY such a flight can score (crossing the end-line plane between crossbar and upright tops). While
   * set, the ball touching the ground or crossing the end-line plane outside the window = FIELD_GOAL_MISS (PAT:
   * CONVERSION_FAILED). Cleared when anyone catches it (offense catch beyond the LOS = live pass, the Fake FG;
   * defense catch = interception).
   */
  fgAttempt: boolean;
  /** Hail Mary flight (huge glowing arc; wider catch radius for the thrower). */
  hailMary: boolean;
  /** Bit 0 = touched by player 0 this flight, bit 1 = player 1. */
  touchedMask: number;
  lastTouchedBy: number;
  bounces: number;
  /**
   * Classic: this ball is a KICK (kickoff / onside / punt toss) nobody has possessed yet — in flight (AIR) or
   * bouncing on the ground (LOOSE). Only the receiver may touch it in the air (any open glove catches); on the
   * ground it is a free ball by the KICK rules. Cleared when someone possesses it or the play ends.
   */
  kick: boolean;
  /** Frames since the kick first touched the ground (LOOSE), 0 before (KICK.looseDeadFrames). */
  looseFrames: number;
}

// ── Rules ──────────────────────────────────────────────────────────────────────────────────────────────

export interface PlayResult {
  kind: PlayResultKind;
  /** The player credited (tackler, catcher, scorer…), -1 none. */
  actor: number;
  /** Net yards for the offense (negative = loss). */
  yards: number;
  /** Points scored on the play. */
  points: number;
  /** Who scored (-1 none). */
  scorer: number;
  /** Spot where the ball became dead. */
  spotX: number;
  /** The play earned a first down. */
  firstDown: boolean;
}

export interface RulesSim {
  phase: Phase;
  /** Frames since entering `phase`. */
  phaseFrame: number;
  format: number;
  /** Player index on offense. */
  offense: number;
  /** +1 = offense attacks the right end zone (x = 100), -1 = the left (x = 0). */
  attackDir: number;
  /** Possessions started by each player (Drives format). */
  drivesStarted: number[];
  drivesPerPlayer: number;
  /** x where the current drive started. */
  driveStartX: number;
  down: number;
  /** Yards to gain for a first down (to the goal line when it's "& goal"). */
  distance: number;
  /** Line of scrimmage (x). */
  los: number;
  /** First-down line (x); equals the goal line when the line to gain is inside the end zone ("& GOAL"). */
  firstDownX: number;
  goalToGo: boolean;
  /** Pre-snap play clock frames remaining. */
  playClock: number;
  /** Rush count frames remaining (RUSH_COUNT phase). */
  rushCount: number;
  /** Blitz was used: the rush count is cut to the 18 f telegraph this play (SPECIALS_TUNING.blitz.rushFrames). */
  rushSkipped: boolean;
  /** PlayCall declared at the snap (NORMAL before it; PAT_TOSS for a 1-pt try). */
  playCall: PlayCall;
  /** Wind for this possession, yd/s² on the ball in flight: config.wind × attackDir (set by startDrive). */
  windX: number;
  /** The offense crossed the LOS (ends the rush count immediately). */
  offenseCrossed: boolean;
  /** A forward pass was already thrown this play. */
  passThrown: boolean;
  /** Tick of the snap (-1 before the first snap). */
  snapTick: number;
  /** Plays run (snaps), for stats/replay ids. */
  playCount: number;
  score: number[];
  conversion: ConversionChoice;
  /** The current play is a conversion attempt (TOSS or TWO_POINT). */
  conversionPlay: boolean;
  overtime: boolean;
  /** Overtime round (pairs of possessions), 0 in regulation. */
  otRound: number;
  /** What happens when DEAD ends. */
  afterDead: AfterDead;
  lastResult: PlayResult;
  /** Winner when GAME_OVER (-1 until then). */
  winner: number;
  /** Classic format: game clock frames remaining and half (1/2). Unused in Drives. */
  gameClock: number;
  half: number;
  /**
   * Classic: the game clock is running. Set at the snap (kicks: when the toss is away); live play always counts it
   * down; it keeps running between plays only after a tackle in bounds (RULES.classicHalfFrames).
   */
  clockRunning: boolean;
  // ── Rules-owned bookkeeping (written ONLY by src/rules; other modules signal through their own fields) ──
  /** ball.state at the end of the previous rulesPost (AIR → HELD between two calls = a catch/interception). */
  prevBallState: number;
  /** ball.touchedMask at the end of the previous rulesPost (a new non-thrower bit while AIR = swat). */
  prevTouchedMask: number;
  /** players[i].statBaits already converted into power (+POWER.bait per new bait). */
  baitsCounted: number[];
  /** Per-yard power earned by each player this play, not yet announced (one POWER_GAIN{YARDS} at PLAY_END). */
  yardPower: number[];
  /** The offense completed a forward pass this play (legal catch beyond the LOS). */
  completion: boolean;
  /** x of that catch (pass-yards stat). */
  catchX: number;
}

/** Deterministic camera target; the renderer smooths/shakes it. */
export interface CameraHint {
  x: number;
  y: number;
  zoom: number;
  focus: CameraFocus;
}

/** Copy of the MatchConfig as numbers (strings never live in SimState). */
export interface SimConfig {
  seed: number;
  format: number;
  normalizedStats: boolean;
  specials: number;
  drivesPerPlayer: number;
  firstPossession: number;
  /** CharacterIndex for players 0 and 1. */
  characters: number[];
  /** MatchConfig.wind (yd/s², signed). */
  wind: number;
}

export interface SimState {
  version: number;
  /** Ticks stepped since createInitialState (incremented every step, including during hit-stop). */
  tick: number;
  /** Hit-stop frames remaining (whole-sim freeze; see invariant 7). */
  hitStop: number;
  prng: PrngState;
  config: SimConfig;
  /** Exactly two players: index 0 = HOME (red), 1 = AWAY (blue). */
  players: [PlayerSim, PlayerSim];
  ball: BallSim;
  rules: RulesSim;
  camera: CameraHint;
}

// ── Factories ──────────────────────────────────────────────────────────────────────────────────────────

const zeros = (n: number): number[] => new Array<number>(n).fill(0);
const minusOnes = (n: number): number[] => new Array<number>(n).fill(-1);

export const createBlankPlayer = (index: number): PlayerSim => ({
  index,
  character: 0,
  stats: { speedMul: 1, jumpMul: 1, armMul: 1, tackleMul: 1, tossMul: 1, headRadius: 0.9 },
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  facing: 1,
  grounded: true,
  state: PlayerState.IDLE,
  stateFrame: 0,
  prevState: PlayerState.IDLE,
  airFrame: 0,
  hurdling: false,
  hopPhase: 0,
  dashDir: 0,
  stillFrames: 0,
  burstFrames: 0,
  throwCharge: 0,
  lob: false,
  truckCharge: 0,
  stunFrames: 0,
  iFrames: 0,
  spinDodged: false,
  beatenFrames: 0,
  beatenTick: -1,
  glovePose: GlovePose.READY,
  gloveX: 0,
  gloveY: 0,
  gloveTargetX: 0,
  gloveTargetY: 0,
  stamina: 0,
  power: 0,
  special: {
    id: SpecialId.NONE,
    phase: SpecialPhase.IDLE,
    frame: 0,
    timer: 0,
    decoyActive: false,
    decoyX: 0,
    decoyY: 0,
    decoyVx: 0,
    decoyVy: 0,
    decoyFacing: 1,
    decoyFrames: 0,
    cooldownFrames: 0,
    usesThisDrive: 0,
  },
  input: {
    cur: 0,
    prev: 0,
    lastPress: minusOnes(BUTTON_COUNT),
    prevPress: minusOnes(BUTTON_COUNT),
    held: zeros(BUTTON_COUNT),
    buffer: zeros(BUTTON_COUNT),
  },
  pumpFakesUsed: 0,
  hardCountsUsed: 0,
  crossedLos: false,
  feintTick: -1,
  feintKind: FeintKind.NONE,
  commitTick: -1,
  commitKind: CommitKind.NONE,
  playBestX: 0,
  flags: 0,
  statYards: 0,
  statPassYards: 0,
  statTackles: 0,
  statCatches: 0,
  statInterceptions: 0,
  statSacks: 0,
  statBaits: 0,
  statTouchdowns: 0,
});

const createBlankResult = (): PlayResult => ({
  kind: PlayResultKind.NONE,
  actor: -1,
  yards: 0,
  points: 0,
  scorer: -1,
  spotX: 0,
  firstDown: false,
});

/** A state with the full shape and neutral values. Used for snapshot buffers (`cloneState`) and by createInitialState. */
export const createBlankState = (): SimState => ({
  version: SIM_STATE_VERSION,
  tick: 0,
  hitStop: 0,
  prng: createPrngState(0),
  config: {
    seed: 0,
    format: 0,
    normalizedStats: false,
    specials: 0,
    drivesPerPlayer: 0,
    firstPossession: 0,
    characters: zeros(2),
    wind: 0,
  },
  players: [createBlankPlayer(0), createBlankPlayer(1)],
  ball: {
    state: BallState.HELD,
    holder: -1,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    angle: 0,
    angVel: 0,
    thrownBy: -1,
    airFrames: 0,
    releaseX: 0,
    forwardPass: false,
    passedLos: false,
    fgAttempt: false,
    hailMary: false,
    touchedMask: 0,
    lastTouchedBy: -1,
    bounces: 0,
    kick: false,
    looseFrames: 0,
  },
  rules: {
    phase: Phase.PRE_SNAP,
    phaseFrame: 0,
    format: 0,
    offense: 0,
    attackDir: 1,
    drivesStarted: zeros(2),
    drivesPerPlayer: 0,
    driveStartX: 0,
    down: 1,
    distance: YARDS_FOR_FIRST_DOWN,
    los: 0,
    firstDownX: 0,
    goalToGo: false,
    playClock: 0,
    rushCount: 0,
    rushSkipped: false,
    playCall: PlayCall.NORMAL,
    windX: 0,
    offenseCrossed: false,
    passThrown: false,
    snapTick: -1,
    playCount: 0,
    score: zeros(2),
    conversion: ConversionChoice.NONE,
    conversionPlay: false,
    overtime: false,
    otRound: 0,
    afterDead: AfterDead.NEXT_DOWN,
    lastResult: createBlankResult(),
    winner: -1,
    gameClock: 0,
    half: 1,
    clockRunning: false,
    prevBallState: BallState.HELD,
    prevTouchedMask: 0,
    baitsCounted: zeros(2),
    yardPower: zeros(2),
    completion: false,
    catchX: 0,
  },
  camera: { x: 0, y: 0, zoom: 1, focus: CameraFocus.BALL },
});

/**
 * Place both players at the line of scrimmage for a new down: offense behind the LOS facing `attackDir`, defense
 * across the neutral gap facing the offense, ball in the offense's glove. Resets per-play flags. Pure helper
 * shared by createInitialState and the rules module.
 *
 * Specials: ANY phase (ARMED, TELEGRAPH, ACTIVE, SPENT) returns to IDLE with timer 0 — one special per play. Power
 * spent on a TELEGRAPH/ACTIVE special that never fired stays spent (the rules emit SPECIAL_END{consumed:false}
 * at PLAY_END for it); an ARMED Blitz never spent its power (the snap never came), so nothing is lost.
 * playCall: PAT_TOSS when `rules.conversion` is TOSS, else NORMAL (FG_TOSS is chosen at the snap).
 */
export const lineUpForSnap = (s: SimState, kickoff = false): void => {
  const r = s.rules;
  const dir = r.attackDir;
  for (let i = 0; i < 2; i++) {
    const p = s.players[i as 0 | 1];
    const isOffense = i === r.offense;
    // Kickoff: the receiver waits deep (KICK.receiverDepth past the LOS), the kicker at the usual spot.
    p.x = isOffense ? r.los - dir * LINEUP_GAP : r.los + dir * (kickoff ? KICK.receiverDepth : LINEUP_GAP);
    p.y = p.stats.headRadius;
    p.vx = 0;
    p.vy = 0;
    p.facing = isOffense ? dir : -dir;
    p.grounded = true;
    p.prevState = p.state;
    p.state = PlayerState.IDLE;
    p.stateFrame = 0;
    p.airFrame = 0;
    p.hurdling = false;
    p.hopPhase = 0;
    p.dashDir = 0;
    p.stillFrames = 0;
    p.burstFrames = 0;
    p.throwCharge = 0;
    p.lob = false;
    p.truckCharge = 0;
    p.stunFrames = 0;
    p.iFrames = 0;
    p.spinDodged = false;
    p.beatenFrames = 0;
    p.beatenTick = -1;
    p.glovePose = isOffense ? GlovePose.GRIP : GlovePose.READY;
    p.gloveX = isOffense ? GLOVE.holdBall.ox : GLOVE.idle.ox;
    p.gloveY = isOffense ? GLOVE.holdBall.oy : GLOVE.idle.oy;
    p.gloveTargetX = p.gloveX;
    p.gloveTargetY = p.gloveY;
    p.stamina = STAMINA.max;
    p.pumpFakesUsed = 0;
    p.hardCountsUsed = 0;
    p.crossedLos = false;
    p.feintTick = -1;
    p.feintKind = FeintKind.NONE;
    p.commitTick = -1;
    p.commitKind = CommitKind.NONE;
    p.playBestX = r.los;
    const sp = p.special;
    sp.phase = SpecialPhase.IDLE;
    sp.frame = 0;
    sp.timer = 0;
    sp.decoyActive = false;
    sp.decoyVx = 0;
    sp.decoyVy = 0;
    sp.decoyFrames = 0;
  }
  const carrier = s.players[r.offense as 0 | 1];
  const b = s.ball;
  b.state = BallState.HELD;
  b.holder = r.offense;
  b.x = carrier.x + carrier.facing * carrier.gloveX;
  b.y = carrier.y + carrier.gloveY;
  b.vx = 0;
  b.vy = 0;
  b.angle = 0;
  b.angVel = 0;
  b.thrownBy = -1;
  b.airFrames = 0;
  b.releaseX = 0;
  b.forwardPass = false;
  b.passedLos = false;
  b.fgAttempt = false;
  b.hailMary = false;
  b.touchedMask = 0;
  b.lastTouchedBy = -1;
  b.bounces = 0;
  b.kick = false;
  b.looseFrames = 0;
  r.phase = Phase.PRE_SNAP;
  r.phaseFrame = 0;
  r.playClock = RULES.playClockFrames;
  r.rushCount = RULES.rushCountFrames;
  r.rushSkipped = false;
  r.playCall = kickoff
    ? PlayCall.KICKOFF
    : r.conversion === ConversionChoice.TOSS
      ? PlayCall.PAT_TOSS
      : PlayCall.NORMAL;
  r.offenseCrossed = false;
  r.passThrown = false;
  s.camera.x = r.los;
  s.camera.y = 2;
  s.camera.zoom = 1;
  s.camera.focus = CameraFocus.BALL;
};

/**
 * The direction player `p` attacks: player 0 attacks the right end zone (+x) and player 1 the left, swapped in the
 * second half of a Classic match (and its overtime).
 */
export const attackDirOf = (s: SimState, p: number): number =>
  (p === 0 ? 1 : -1) * (s.rules.format === MatchFormat.CLASSIC && s.rules.half === 2 ? -1 : 1);

/**
 * Give the ball to `offense` (attack direction + this possession's wind). Wind (ARCHITECTURE §5): Drives and every
 * overtime play it per possession (`wind × attackDir`: both players get the same tail/head wind); Classic
 * regulation keeps it fixed in the WORLD (`wind` toward +x), and the ends swap at half-time, so each player has it
 * at his back for one half and in his face for the other.
 */
export const setOffense = (s: SimState, offense: number): void => {
  const r = s.rules;
  r.offense = offense;
  r.attackDir = attackDirOf(s, offense);
  r.windX = r.format === MatchFormat.CLASSIC && !r.overtime ? s.config.wind : s.config.wind * r.attackDir;
};

/**
 * Start a possession for `offense` from `yardsToGoal` yards out (Drives format: 40; overtime: 25; Classic: the
 * spot the last play earned). Sets attackDir (`attackDirOf`), the possession's wind, down & distance, and lines up.
 */
export const startDrive = (s: SimState, offense: number, yardsToGoal: number): void => {
  const r = s.rules;
  setOffense(s, offense);
  r.los = xFromYardsToGoal(yardsToGoal, r.attackDir);
  r.driveStartX = r.los;
  r.down = 1;
  setLineToGain(s);
  r.drivesStarted[offense] = (r.drivesStarted[offense] ?? 0) + 1;
  r.conversion = ConversionChoice.NONE;
  r.conversionPlay = false;
  // Blitz (defense): one arm per drive (handegg-roster.md), reset for the new drive.
  s.players[0].special.usesThisDrive = 0;
  s.players[1].special.usesThisDrive = 0;
  lineUpForSnap(s);
};

/**
 * Classic: line up a KICKOFF by `kicker` from `yardsToGoal` out (his own 35; his own 20 after a safety): PRE_SNAP
 * with playCall KICKOFF, the receiver deep. Not a drive (drivesStarted unchanged); down & distance are cosmetic
 * (the HUD shows KICKOFF while playCall is a kick).
 */
export const startKickoff = (s: SimState, kicker: number, yardsToGoal: number): void => {
  const r = s.rules;
  setOffense(s, kicker);
  r.los = xFromYardsToGoal(yardsToGoal, r.attackDir);
  r.driveStartX = r.los;
  r.down = 1;
  setLineToGain(s);
  r.conversion = ConversionChoice.NONE;
  r.conversionPlay = false;
  lineUpForSnap(s, true);
};

/** Recompute firstDownX/distance/goalToGo from the LOS for a fresh set of downs. */
export const setLineToGain = (s: SimState): void => {
  const r = s.rules;
  const goal = goalLineFor(r.attackDir);
  const target = r.los + r.attackDir * YARDS_FOR_FIRST_DOWN;
  const beyondGoal = r.attackDir > 0 ? target >= goal : target <= goal;
  r.goalToGo = beyondGoal;
  r.firstDownX = beyondGoal ? goal : target;
  r.distance = Math.abs(r.firstDownX - r.los);
};

/** Build the initial state for a match. Allocates; call once per match (and per snapshot buffer via cloneState). */
export const createInitialState = (config: MatchConfig): SimState => {
  const s = createBlankState();
  const c = s.config;
  c.seed = config.seed >>> 0;
  c.format = config.format;
  c.normalizedStats = config.normalizedStats;
  c.specials = config.specials;
  c.drivesPerPlayer = config.drivesPerPlayer;
  c.firstPossession = config.firstPossession;
  c.wind = config.wind;
  c.characters[0] = characterIndexOf(config.characters[0]);
  c.characters[1] = characterIndexOf(config.characters[1]);

  const seeded = createPrngState(c.seed);
  s.prng.a = seeded.a;
  s.prng.b = seeded.b;
  s.prng.c = seeded.c;
  s.prng.d = seeded.d;

  for (let i = 0; i < 2; i++) {
    const p = s.players[i as 0 | 1];
    const ch = c.characters[i] as CharacterIndex;
    p.character = ch;
    const st = resolveStats(ch, c.normalizedStats);
    p.stats.speedMul = st.speedMul;
    p.stats.jumpMul = st.jumpMul;
    p.stats.armMul = st.armMul;
    p.stats.tackleMul = st.tackleMul;
    p.stats.tossMul = st.tossMul;
    p.stats.headRadius = st.headRadius;
    // STANDARD (shared pool) is reserved: treated as OFF until the pool exists.
    p.special.id = c.specials === SpecialsMode.SIGNATURE ? CHARACTERS[ch].signature : SpecialId.NONE;
    p.special.phase = SpecialPhase.IDLE;
    p.stamina = STAMINA.max;
    p.power = 0;
  }

  const r = s.rules;
  r.format = c.format;
  r.drivesPerPlayer = c.drivesPerPlayer;
  r.score[0] = 0;
  r.score[1] = 0;
  r.half = 1;
  r.gameClock = RULES.classicHalfFrames;
  r.clockRunning = false;
  // Classic opens with a kickoff TO the player who has the ball first; Drives with his first drive.
  if (c.format === MatchFormat.CLASSIC) startKickoff(s, 1 - c.firstPossession, KICKOFF_YARDS_TO_GOAL);
  else startDrive(s, c.firstPossession, DRIVE_START_YARDS_TO_GOAL);
  return s;
};
